<template>
  <div class="blog-admin-list">
    <h3 class="mb-3">Gestionar Entradas del Blog</h3>

    <div v-if="loading" class="text-center my-4">
      <b-spinner label="Cargando..." variant="primary"></b-spinner>
    </div>
    <b-alert v-else-if="error" show variant="danger">{{ error }}</b-alert>
    <b-alert v-else-if="posts.length === 0" show variant="info">No hay entradas para mostrar.</b-alert>

    <div v-else class="table-responsive">
      <table class="table table-striped table-hover table-sm">
        <thead>
          <tr>
            <th>ID</th>
            <th>Título</th>
            <th>Slug</th>
            <th>Estado</th>
            <th>Fecha Creación</th>
            <th>Fecha Actualización</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="post in posts" :key="post.id">
            <td>{{ post.id }}</td>
            <td>{{ post.title }}</td>
            <td>{{ post.slug }}</td>
            <td>
              <span :class="post.published_at ? 'badge bg-success' : 'badge bg-secondary'">
                {{ post.published_at ? 'Publicado' : 'Borrador' }}
              </span>
              <small v-if="post.published_at" class="d-block text-muted">{{ formatDate(post.published_at) }}</small>
            </td>
            <td>{{ formatDate(post.created_at) }}</td>
            <td>{{ formatDate(post.updated_at) }}</td>
            <td>
              <b-button size="sm" variant="primary" @click="handleEditClick(post.id)" class="me-1" :disabled="editingPostId === post.id">
                 <b-spinner small v-if="editingPostId === post.id"></b-spinner>
                 <font-awesome-icon v-else icon="pen-to-square" /> Editar
              </b-button>
              <b-button size="sm" variant="danger" @click="$emit('delete-post', post.id, post.title)">
                <font-awesome-icon icon="trash-can" /> Eliminar
              </b-button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, defineEmits, defineProps, watch } from 'vue';
import { BSpinner, BAlert, BButton } from 'bootstrap-vue-next';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getAllBlogPosts, getAdminBlogPostDetails } from '../../../services/blogService.js';

const props = defineProps({
  refreshCounter: {
      type: Number,
      default: 0
  }
});

const emit = defineEmits(['edit-post', 'delete-post', 'load-error']);

const posts = ref([]);
const loading = ref(true);
const error = ref(null);
const editingPostId = ref(null);

async function fetchAllPosts() {
  loading.value = true;
  error.value = null;
  try {
    const token = localStorage.getItem('jwt_token');
    posts.value = await getAllBlogPosts(token);
  } catch (err) {
    error.value = `Error al cargar entradas: ${err.message}`;
    posts.value = [];
    emit('load-error', error.value);
  } finally {
    loading.value = false;
  }
}
 
 async function handleEditClick(postId) {
     editingPostId.value = postId;
     error.value = null;
     try {
         const token = localStorage.getItem('jwt_token');
         const fullPost = await getAdminBlogPostDetails(postId, token);
         emit('edit-post', fullPost);
     } catch (err) {
         error.value = `Error al cargar detalles para editar: ${err.message}`;
     } finally {
         editingPostId.value = null;
     }
}

function formatDate(dateString) {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('es-ES', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    });
  } catch (e) { return dateString; }
}

onMounted(fetchAllPosts);
watch(() => props.refreshCounter, fetchAllPosts);

</script>

<style scoped>
.blog-admin-list table {
    width: 100%; /* Asegura que la tabla use el ancho completo del contenedor */
    table-layout: auto; /* Permite que el ancho de las columnas se ajuste al contenido */
}

.table-sm td, .table-sm th {
  padding: 0.4rem;
  font-size: 0.9em;
  white-space: nowrap; /* Evita que el texto se divida en varias líneas */
  overflow: hidden; /* Oculta el contenido que desborda */
  text-overflow: ellipsis; /* Muestra puntos suspensivos para el contenido oculto */
}

.table-sm th:nth-child(2), /* Título */
.table-sm td:nth-child(2) {
    width: 30%; /* Ajusta el ancho de la columna del título */
}

.table-sm th:nth-child(3), /* Slug */
.table-sm td:nth-child(3) {
    width: 20%; /* Ajusta el ancho de la columna del slug */
}


.badge {
    font-size: 0.8em;
}
</style>